import { Box, Typography } from "@mui/material";

import { blocks, spans } from "../markdown.js";

/**
 * An agent's instructions, as they were written.
 *
 * ## Why this is not `marked` plus `dompurify`
 *
 * Because both of those end at `dangerouslySetInnerHTML`, and this is the
 * console a customer governs their data through. Rendering a stored field by
 * turning it into HTML and injecting it makes the answer to *"can a string in
 * the kernel put markup on this page"* into *"not if the sanitiser is
 * configured correctly"* — which is a sentence this product spends every
 * other screen refusing to write.
 *
 * This emits React elements from the parse in `../markdown.js`. There is no
 * HTML string at any point, so there is no injection surface to sanitise and
 * nothing to keep patched. It also costs the bundle nothing and inherits the
 * theme, so a heading in a prompt is the same heading as everywhere else.
 *
 * The parse is separate because the property worth testing — **nothing is
 * ever dropped** — is a property of the parse, and `markdown.test.js` asserts
 * it against malformed input without needing a DOM. This file is the part
 * that is obviously correct by inspection.
 *
 * ## What it deliberately does not do
 *
 * No links, no images, no tables, no HTML passthrough. A link in a system
 * prompt is text; making it clickable would invite somebody to follow a URL
 * that an agent's configuration put on the page.
 */

const HEADING_SX = {
  1: { fontSize: "1.05rem", fontWeight: 600, mt: 2.5 },
  2: { fontSize: "0.975rem", fontWeight: 600, mt: 2.25 },
  3: { fontSize: "0.9rem", fontWeight: 600, mt: 2 },
};

function Spans({ text, keyPrefix }) {
  return spans(text).map((span, n) => {
    const key = `${keyPrefix}-${n}`;
    if (span.kind === "code") {
      return (
        <Typography
          key={key}
          component="code"
          variant="mono"
          sx={{
            px: 0.5,
            py: 0.125,
            borderRadius: 0.5,
            backgroundColor: "action.hover",
            overflowWrap: "anywhere",
          }}
        >
          {span.text}
        </Typography>
      );
    }
    if (span.kind === "strong") {
      return (
        <Box key={key} component="strong" sx={{ fontWeight: 600 }}>
          {span.text}
        </Box>
      );
    }
    if (span.kind === "em") {
      return (
        <Box key={key} component="em">
          {span.text}
        </Box>
      );
    }
    return <Box key={key} component="span">{span.text}</Box>;
  });
}

export default function Markdown({ children, sx }) {
  return (
    <Box sx={{ minWidth: 0, "& > :first-of-type": { mt: 0 }, ...sx }}>
      {blocks(children).map((block, index) => {
        const key = `b${index}`;

        if (block.kind === "code") {
          return (
            <Typography
              key={key}
              component="pre"
              variant="mono"
              sx={{
                my: 1.5,
                p: 1.25,
                borderRadius: 1,
                backgroundColor: "action.hover",
                overflowX: "auto",
                whiteSpace: "pre",
              }}
            >
              {block.body}
            </Typography>
          );
        }

        if (block.kind === "heading") {
          return (
            <Typography
              key={key}
              component={`h${Math.min(block.level + 1, 6)}`}
              sx={{
                mb: 0.75,
                lineHeight: 1.35,
                ...(HEADING_SX[block.level] || HEADING_SX[3]),
              }}
            >
              <Spans text={block.body} keyPrefix={key} />
            </Typography>
          );
        }

        if (block.kind === "list") {
          return (
            <Box
              key={key}
              component={block.ordered ? "ol" : "ul"}
              sx={{ my: 1, pl: 3, "& li": { mb: 0.5 } }}
            >
              {block.items.map((item, n) => (
                <Typography key={`${key}-${n}`} component="li" variant="body2">
                  <Spans text={item} keyPrefix={`${key}-${n}`} />
                </Typography>
              ))}
            </Box>
          );
        }

        return (
          <Typography key={key} variant="body2" sx={{ my: 1, lineHeight: 1.65 }}>
            <Spans text={block.body} keyPrefix={key} />
          </Typography>
        );
      })}
    </Box>
  );
}
